/**
 * Trace selection / undo types and small pure helpers shared by EEGViewer
 * and useEEGTraceChannelSelection.
 */
import type {
  BadChannelMark,
  FilterOverride,
} from "../components/customization/customizationTypes";
import type { ChannelRegionKey } from "../components/eegViewerChromeShared";

export const TRACE_TEMP_HIGHLIGHT_DEFAULT = "#facc15";

export type TraceSelectionUndoEntry =
  | {
      kind: "mark_bad";
      label: string;
      channels: string[];
      previousBadByChannel: Record<string, BadChannelMark | null>;
    }
  | {
      kind: "apply_filter";
      label: string;
      channels: string[];
      previousFilterByChannel: Record<string, FilterOverride | null>;
    }
  | {
      kind: "remove_filter";
      label: string;
      channels: string[];
      previousFilterByChannel: Record<string, FilterOverride | null>;
    }
  | {
      kind: "temp_highlight";
      label: string;
      channels: string[];
      previousHighlightByChannel: Record<string, string | null>;
    }
  | {
      kind: "selection_bulk";
      label: string;
      previousSelection: string[];
    }
  | {
      kind: "reset_overrides";
      label: string;
      channels: string[];
      previousChannelGainOverrides: Record<string, number>;
      previousHighlightByChannel: Record<string, string>;
      previousProfileChannelPositionOffsets: Record<string, number>;
      previousProfileChannelColors: Record<string, string>;
      previousFilterByChannel: Record<string, FilterOverride | null>;
    };

export function dedupeLabelsInOrder(labels: string[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  labels.forEach((label) => {
    if (!label || seen.has(label)) return;
    seen.add(label);
    ordered.push(label);
  });
  return ordered;
}

export function areSameLabelSelections(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((item, index) => item === b[index]);
}

export function extractChannelLabelTokens(label: string): string[] {
  return label
    .split(/[^A-Za-z0-9]+/)
    .map((token) => token.trim().toUpperCase())
    .filter(Boolean);
}

export function channelLabelMatchesRegion(
  label: string,
  region: ChannelRegionKey,
): boolean {
  const tokens = extractChannelLabelTokens(label);
  if (!tokens.length) return false;
  return tokens.some((token) => {
    switch (region) {
      case "frontal":
        return /^(FP|AF|F|FC)/.test(token);
      case "temporal":
        return /^(T|FT|TP)/.test(token);
      case "central":
        return /^(C|CZ|FC|CP)/.test(token);
      case "parietal":
        return /^(P|CP|PO)/.test(token) && !/^FP/.test(token);
      case "occipital":
        return /^(O|PO)/.test(token);
      default:
        return false;
    }
  });
}

export function normalizeChannelPairKey(channelPair: string): string {
  return channelPair
    .trim()
    .replace(/\s+/g, "")
    .replace(/-+$/, "")
    .toLowerCase();
}

export function normalizeHexColor(color: unknown): string | null {
  if (typeof color !== "string") return null;
  const value = color.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  return value.toLowerCase();
}
